import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../../lib/api';
import type {
  MessageThread,
  MessageConfirmedStatusResponse,
  SendMessageRequest,
  SendMessageResponse,
  FamilyMessage,
} from '../../lib/api';
import type { ApiError } from '@shared/types';

/**
 * メッセージスレッドを取得するカスタムフック
 */
export const useMessagesThread = (
  patientId: number | null,
  options?: { limit?: number; enabled?: boolean }
) => {
  return useQuery<MessageThread>({
    queryKey: ['messages', 'thread', patientId],
    queryFn: async () => {
      if (!patientId) {
        throw new Error('Patient ID is required');
      }
      return await apiClient.getMessagesThread(patientId, { limit: options?.limit });
    },
    enabled: patientId !== null && !!apiClient.getIdToken() && (options?.enabled ?? true),
    staleTime: 30000, // 30秒
    refetchInterval: 60000, // 1分ごとに自動更新
    retry: 1,
  });
};

/**
 * 確認済みステータスの軽量ポーリングhook
 * 15秒間隔でステータスのみを取得し、既存キャッシュを直接更新する
 */
export const useMessageConfirmedStatus = (patientId: number | null) => {
  const queryClient = useQueryClient();

  const query = useQuery<MessageConfirmedStatusResponse>({
    queryKey: ['messages', 'confirmed-status', patientId],
    queryFn: async () => {
      if (!patientId) {
        throw new Error('Patient ID is required');
      }
      return await apiClient.getMessageConfirmedStatus(patientId);
    },
    enabled: patientId !== null && !!apiClient.getIdToken(),
    staleTime: 10000, // 10秒
    refetchInterval: 15000, // 15秒ごとにポーリング
    refetchIntervalInBackground: false, // バックグラウンド時は停止
  });

  // ステータス変更時に既存のスレッドキャッシュを更新
  useEffect(() => {
    if (!query.data || !patientId) return;

    const statuses = query.data.statuses;
    const currentThread = queryClient.getQueryData<MessageThread>([
      'messages', 'thread', patientId,
    ]);

    if (!currentThread?.threads) return;

    // statusesをMapに変換してO(1)探索
    const statusMap = new Map<string, string | null>();
    for (const s of statuses) {
      statusMap.set(`${s.thread_id}:${s.message_id}`, s.confirmed_at);
    }

    // confirmed_atの変更を検出してキャッシュを更新（確認追加・取り消し双方向）
    let hasChange = false;
    const updatedThreads = currentThread.threads.map(thread => {
      const updatedMessages = thread.messages.map(msg => {
        if (msg.direction !== 'outgoing') return msg;

        const key = `${thread.thread_id}:${msg.message_id}`;
        if (!statusMap.has(key)) return msg;

        const confirmedAt = statusMap.get(key);

        if (confirmedAt && !msg.confirmed_at) {
          // 確認済みに変更
          hasChange = true;
          return { ...msg, confirmed_at: confirmedAt };
        }
        if (!confirmedAt && msg.confirmed_at) {
          // 確認取り消し
          hasChange = true;
          return { ...msg, confirmed_at: undefined };
        }
        return msg;
      });
      return { ...thread, messages: updatedMessages };
    });

    if (hasChange) {
      queryClient.setQueryData<MessageThread>(
        ['messages', 'thread', patientId],
        { ...currentThread, threads: updatedThreads }
      );
    }
  }, [query.data, patientId, queryClient]);

  return query;
};

/**
 * メッセージ送信用のミューテーションフック
 */
export const useSendMessage = () => {
  const queryClient = useQueryClient();

  return useMutation<SendMessageResponse, ApiError, SendMessageRequest, { previousThread?: MessageThread }>({
    mutationFn: async (data: SendMessageRequest) => {
      return await apiClient.sendMessage(data);
    },
    onMutate: async (newMessage) => {
      // 楽観的更新のためにクエリをキャンセル
      await queryClient.cancelQueries({
        queryKey: ['messages', 'thread', newMessage.patient_id],
      });

      // 現在のデータを取得
      const previousThread = queryClient.getQueryData<MessageThread>([
        'messages',
        'thread',
        newMessage.patient_id,
      ]);

      // 楽観的に新しいメッセージを追加
      if (previousThread && newMessage.thread_id && previousThread.threads) {
        const optimisticMessage: FamilyMessage = {
          message_id: Date.now(), // 仮のID
          direction: 'outgoing',
          content: newMessage.content,
          created_at: new Date().toISOString(),
        };

        const updatedThreads = previousThread.threads.map(t =>
          t.thread_id === newMessage.thread_id
            ? { ...t, messages: [...t.messages, optimisticMessage] }
            : t
        );

        queryClient.setQueryData<MessageThread>(
          ['messages', 'thread', newMessage.patient_id],
          {
            ...previousThread,
            threads: updatedThreads,
          }
        );
      }

      return { previousThread };
    },
    onError: (err, newMessage, context) => {
      // エラー時はロールバック
      if (context?.previousThread) {
        queryClient.setQueryData(
          ['messages', 'thread', newMessage.patient_id],
          context.previousThread
        );
      }
      // BE が service_ended 由来の 403 を返した場合、linkedPatients を再取得して
      // FE 側のトルツメ判定を最新の service_end_date に基づき更新する。
      // ApiClient の interceptor が AxiosError を ApiError に変換するため、
      // useMutation の error 型引数は ApiError に揃えてある（キャスト不要）。
      const reason = err.details?.reason;
      if (err.statusCode === 403 && reason === 'service_ended') {
        queryClient.invalidateQueries({ queryKey: ['linkedPatients'] });
      }
    },
    onSettled: (_data, _error, variables) => {
      // 成功・失敗に関わらずクエリを無効化して最新データを取得
      queryClient.invalidateQueries({
        queryKey: ['messages', 'thread', variables.patient_id],
      });
      queryClient.invalidateQueries({
        queryKey: ['messages', 'threads', variables.patient_id],
      });
      if (variables.thread_id) {
        queryClient.invalidateQueries({
          queryKey: ['messages', 'thread-messages', variables.thread_id],
        });
      }
    },
  });
};

export default useMessagesThread;
